import React, { useEffect, useRef } from "react";
import { CheckCircle2 } from "lucide-react";
import { useLandingTelemetry } from "../../../hooks/useLandingTelemetry";

// Telemetry child component
function HeroTelemetry() {
  const { recordCtaClick } = useLandingTelemetry({ pageId: "hero_unified" });
  const hasRecorded = useRef(false);

  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      if (hasRecorded.current) return;

      const target = e.target as HTMLElement;
      const closestAnchor = target.closest('a');
      const closestButton = target.closest('button');

      if (closestButton?.type === "submit" || closestAnchor?.href.includes("mailto")) {
        recordCtaClick();
        hasRecorded.current = true;
      }
    };
    
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [recordCtaClick]);

  return null;
}

export default function HeroSection() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-28 relative">
      <HeroTelemetry />
      
      {/* Identity Badge */}
      <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-brand-sand border-l-4 border-brand-burgundy rounded-r-sm mb-8 select-none shadow-sm">
        <span className="text-brand-burgundy font-mono text-xs uppercase tracking-wider font-bold">
          Fractional CAIO and CTO services for LegalTech and FinTech
        </span>
      </div>

      {/* Majestic Bold Editorial Title */}
      <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-brand-charcoal max-w-5xl leading-[1.1] font-display">
        Production-Grade AI Architecture & <span className="text-brand-burgundy underline decoration-brand-orange decoration-4 underline-offset-8">Executive Fiduciary Oversight</span>
      </h1>

      {/* Subtitle & Editorial Description */}
      <div className="mt-8 space-y-6 max-w-3xl">
        <p className="text-xl sm:text-2xl text-brand-charcoal font-semibold leading-snug">
          Fractional CAIO and CTO services for VC-backed and bootstrapped LegalTech and FinTech enterprises deploying $500K to $20M in AI infrastructure.
        </p>
        <p className="text-base sm:text-lg md:text-xl text-stone-600 leading-relaxed font-light">
          AI investments fail when probabilistic models are deployed without deterministic infrastructure and capital governance. I provide executive technical leadership to design, audit, and deploy audit-ready AI systems. By aligning GraphRAG architectures, memory-safe backends, and strict capital allocation controls, I ensure every dollar spent on AI delivers measurable operational efficiency and compliance.
        </p>

        {/* Fast action links / tags styled like menu categories */}
        <div className="flex flex-wrap gap-3">
          <span className="px-4 py-2 bg-brand-sand border border-brand-sand hover:border-brand-burgundy/30 rounded-sm text-xs font-mono font-bold text-brand-burgundy transition-colors cursor-default uppercase tracking-wider">
            ✦ AI Systems Architecture
          </span>
          <span className="px-4 py-2 bg-brand-sand border border-brand-sand hover:border-brand-burgundy/30 rounded-sm text-xs font-mono font-bold text-brand-burgundy transition-colors cursor-default uppercase tracking-wider">
            ✦ Governance
          </span>
          <span className="px-4 py-2 bg-brand-sand border border-brand-sand hover:border-brand-burgundy/30 rounded-sm text-xs font-mono font-bold text-brand-burgundy transition-colors cursor-default uppercase tracking-wider">
            ✦ Organizational Intelligence
          </span>
        </div>
      </div>

      {/* Franchise Board Statistics / Key Trust Indicators */}
      <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl border-t-2 border-brand-sand pt-10">
        <div className="p-1">
          <div className="text-[10px] font-mono text-stone-400 uppercase tracking-widest font-bold">CAPITAL SCOPE</div>
          <div className="text-lg sm:text-xl font-bold text-brand-charcoal font-display mt-1 flex items-center gap-1.5 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> $500K–$20M Deployments
          </div>
        </div>
        <div className="p-1">
          <div className="text-[10px] font-mono text-stone-400 uppercase tracking-widest font-bold">TARGET SECTORS</div>
          <div className="text-lg sm:text-xl font-bold text-brand-charcoal font-display mt-1 flex items-center gap-1.5 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> LegalTech & FinTech
          </div>
        </div>
        <div className="p-1">
          <div className="text-[10px] font-mono text-stone-400 uppercase tracking-widest font-bold">INFRASTRUCTURE</div>
          <div className="text-lg sm:text-xl font-bold text-brand-charcoal font-display mt-1 flex items-center gap-1.5 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> GraphRAG & Rust Backends
          </div>
        </div>
        <div className="p-1">
          <div className="text-[10px] font-mono text-stone-400 uppercase tracking-widest font-bold">ADVISORY NETWORK</div>
          <div className="text-lg sm:text-xl font-bold text-brand-charcoal font-display mt-1 flex items-center gap-1.5 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> Tegus / AlphaSights
          </div>
        </div>
      </div>
    </section>
  );
}
